import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Phone, AlertCircle, Wallet, ShoppingBag, TrendingUp, Clock, CheckCircle, FileText, Link as LinkIcon, Star } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getUserProfile, updateUserProfile, uploadProfilePicture, deleteProfilePicture, updateProfilePictureUrl } from '../../services/profileService';
import { dashboardService } from '../../services/dashboardService';
import { ratingService } from '../../services/ratingService';
import Navigation from '../../components/Navigation';
import Input from '../../components/ui/Input';


export default function Profile() {
  const { user, profile, hasConfirmedStatus } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    otherInfo: '',
    valuebookLink: '',
    profilePictureUrl: ''
  });

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    otherInfo: '',
    valuebookLink: ''
  });

  const [dashboardStats, setDashboardStats] = useState({
    walletBalance: 0,
    activeVouchers: 0,
    soldVouchers: 0,
    purchasedVouchers: 0
  });

  const [recentTransactions, setRecentTransactions] = useState([]);
  const [ratingStats, setRatingStats] = useState({ average: null, count: 0 });
  const [recentRatings, setRecentRatings] = useState([]);

  useEffect(() => {
    // CRITICAL FIX: Only fetch data if user and user.id exist
    if (user && user?.id) {
      fetchProfileData();
      fetchDashboardData();
      fetchRatingData();
    }
  }, [user]);

  const fetchProfileData = async () => {
    // CRITICAL FIX: Validate user.id before making API call
    if (!user?.id) {
      console.warn('User ID is not available');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const result = await getUserProfile(user?.id);
      
      if (result?.success) {
        setProfileData(result?.data);
        setFormData({
          fullName: result?.data?.fullName || '',
          email: result?.data?.email || '',
          phoneNumber: result?.data?.phoneNumber || '',
          otherInfo: result?.data?.otherInfo || '',
          valuebookLink: result?.data?.valuebookLink || ''
        });
        setImagePreview(result?.data?.profilePictureUrl || null);
      } else {
        setError(result?.error?.message || 'Greška pri učitavanju podataka');
      }
    } catch (err) {
      console.error('Error loading profile:', err);
      setError(err?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    // CRITICAL FIX: Validate user.id before making API call
    if (!user?.id) {
      console.warn('User ID is not available for dashboard data');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      // Load dashboard stats
      const stats = await dashboardService?.getDashboardStats(user?.id);
      setDashboardStats(stats);

      // Load recent transactions
      const transactions = await dashboardService?.getRecentTransactions(user?.id, 5);
      setRecentTransactions(transactions);

    } catch (err) {
      console.error('Error loading dashboard:', err);
      setError(err?.message || 'Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const fetchRatingData = async () => {
    if (!user?.id) return;
    try {
      const [stats, ratings] = await Promise.all([
        ratingService?.getUserRatingStats(user?.id),
        ratingService?.getUserRatings(user?.id),
      ]);
      setRatingStats(stats);
      setRecentRatings(ratings?.slice(0, 5));
    } catch (err) {
      console.error('Error loading ratings:', err);
    }
  };

  const handleImageChange = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!validTypes?.includes(file?.type)) {
        setError('Nevažeći tip fajla. Molimo koristite JPEG, PNG ili WebP format.');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file?.size > maxSize) {
        setError('Fajl je prevelik. Maksimalna veličina je 5MB.');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader?.result);
      };
      reader?.readAsDataURL(file);
      setError('');
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(profileData?.profilePictureUrl || null);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccessMessage('');
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      fullName: profileData?.fullName || '',
      email: profileData?.email || '',
      phoneNumber: profileData?.phoneNumber || '',
      otherInfo: profileData?.otherInfo || '',
      valuebookLink: profileData?.valuebookLink || ''
    });
    setImagePreview(profileData?.profilePictureUrl || null);
    setError('');
    setSuccessMessage('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      if (!formData?.fullName?.trim()) {
        setError('Puno ime je obavezno');
        return;
      }

      if (formData?.otherInfo && formData?.otherInfo?.length > 50) {
        setError('Ostali podaci ne mogu biti duži od 50 karaktera');
        return;
      }

      setSaving(true);
      setError('');
      setSuccessMessage('');

      try {
        let newProfilePictureUrl = profileData?.profilePictureUrl;

        // Handle profile picture upload if there's a new image
        if (imageFile) {
          setUploadingImage(true);

          // Delete old profile picture if exists
          if (profileData?.profilePictureUrl) {
            await deleteProfilePicture(profileData?.profilePictureUrl, user?.id);
          }

          // Upload new profile picture
          const uploadResult = await uploadProfilePicture(imageFile, user?.id);
          
          if (!uploadResult?.success) {
            throw uploadResult?.error;
          }

          newProfilePictureUrl = uploadResult?.url;

          // Update profile picture URL in database
          const updatePictureResult = await updateProfilePictureUrl(user?.id, newProfilePictureUrl);
          
          if (!updatePictureResult?.success) {
            throw updatePictureResult?.error;
          }

          setUploadingImage(false);
        }

        // Update other profile data
        const result = await updateUserProfile(user?.id, {
          fullName: formData?.fullName,
          email: formData?.email,
          phoneNumber: formData?.phoneNumber,
          otherInfo: formData?.otherInfo,
          valuebookLink: formData?.valuebookLink
        });

        if (result?.success) {
          setProfileData({
            ...result?.data,
            profilePictureUrl: newProfilePictureUrl
          });
          setImageFile(null);
          setSuccessMessage('Profil je uspešno ažuriran!');
          setIsEditing(false);
          
          setTimeout(() => {
            setSuccessMessage('');
          }, 3000);
        } else {
          throw result?.error;
        }
      } catch (err) {
        console.error('Error saving profile:', err);
        setError(err?.message || 'Došlo je do greške prilikom čuvanja profila. Pokušajte ponovo.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err?.message || 'Greška pri ažuriranju profila');
    } finally {
      setSaving(false);
      setUploadingImage(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    })?.format(amount) + ' RSD';
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('sr-RS', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Na čekanju', color: 'bg-yellow-100 text-yellow-800' },
      completed: { label: 'Završeno', color: 'bg-green-100 text-green-800' },
      failed: { label: 'Neuspelo', color: 'bg-red-100 text-red-800' },
      disputed: { label: 'Sporno', color: 'bg-orange-100 text-orange-800' }
    };
    
    const statusInfo = statusMap?.[status] || statusMap?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusInfo?.color}`}>
        {statusInfo?.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Dobrodošao nazad, {profileData?.fullName || 'Korisniče'}!
          </h1>
          <p className="mt-2 text-gray-600 text-sm sm:text-base">Evo pregleda vaših aktivnosti</p>
        </div>

        {/* Profile Data Card */}
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Podaci profila</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 min-h-[44px] bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
              >
                Izmeni
              </button>
            )}
          </div>

          {/* Profile Picture Section */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Slika profila (opciono)
            </label>
            <div className="flex items-center gap-6">
              {/* Image Preview */}
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 border-2 border-gray-300">
                  {imagePreview ? (
                    <img
                      src={imagePreview}
                      alt="Slika profila"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg className="w-16 h-16 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              {/* Upload Controls */}
              {isEditing && (
                <div className="flex-1">
                  <div className="flex gap-3">
                    <label className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Izaberi sliku
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/jpg,image/webp"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>

                    {imageFile && (
                      <button
                        onClick={handleRemoveImage}
                        className="px-4 py-2 border border-red-300 rounded-lg text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
                      >
                        Ukloni
                      </button>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    JPEG, PNG ili WebP. Maksimalno 5MB.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Existing form fields */}
          <div className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <User size={18} className="text-blue-600" />
                  Puno Ime <span className="text-red-500">*</span>
                </div>
              </label>
              {isEditing ? (
                <Input
                  type="text"
                  value={formData?.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e?.target?.value)}
                  placeholder="Unesite vaše puno ime"
                  className="w-full text-base border-2 focus:border-blue-500"
                  required
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 text-lg font-medium">
                    {profileData?.fullName || (
                      <span className="text-gray-400 italic">Nije postavljeno - kliknite "Izmeni Profil" da dodate</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <Mail size={18} className="text-blue-600" />
                  Email Adresa
                </div>
              </label>
              <div className="bg-gray-100 rounded-lg p-4 border border-gray-300">
                <p className="text-gray-700 text-lg">{profileData?.email || 'Nije postavljeno'}</p>
                <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                  <AlertCircle size={14} />
                  Email adresa se ne može menjati
                </p>
              </div>
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <Phone size={18} className="text-blue-600" />
                  Broj Telefona <span className="text-gray-400 text-xs ml-1">(opciono)</span>
                </div>
              </label>
              {isEditing ? (
                <Input
                  type="tel"
                  value={formData?.phoneNumber || ''}
                  onChange={(e) => handleInputChange('phoneNumber', e?.target?.value)}
                  placeholder="Unesite broj telefona (npr. +381 61 234 5678)"
                  className="w-full text-base border-2 focus:border-blue-500"
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 text-lg font-medium">
                    {profileData?.phoneNumber || (
                      <span className="text-gray-400 italic">Nije postavljeno - kliknite "Izmeni Profil" da dodate</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Other Info */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <FileText size={18} className="text-blue-600" />
                  Ostali podaci <span className="text-gray-400 text-xs ml-1">(do 50 karaktera)</span>
                </div>
              </label>
              {isEditing ? (
                <div>
                  <Input
                    type="text"
                    value={formData?.otherInfo || ''}
                    onChange={(e) => handleInputChange('otherInfo', e?.target?.value)}
                    placeholder="Unesite dodatne informacije o sebi"
                    className="w-full text-base border-2 focus:border-blue-500"
                    maxLength={50}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData?.otherInfo?.length || 0}/50 karaktera
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-gray-900 text-lg font-medium">
                    {profileData?.otherInfo || (
                      <span className="text-gray-400 italic">Nije postavljeno - kliknite "Izmeni Profil" da dodate</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* ValueBook Link */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                <div className="flex items-center gap-2">
                  <LinkIcon size={18} className="text-blue-600" />
                  Link na platformu ValueBook <span className="text-gray-400 text-xs ml-1">(opciono)</span>
                </div>
              </label>
              {isEditing ? (
                <Input
                  type="url"
                  value={formData?.valuebookLink || ''}
                  onChange={(e) => handleInputChange('valuebookLink', e?.target?.value)}
                  placeholder="Unesite link ka ValueBook platformi"
                  className="w-full text-base border-2 focus:border-blue-500"
                />
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  {profileData?.valuebookLink ? (
                    <a
                      href={profileData?.valuebookLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700 text-lg font-medium underline flex items-center gap-2"
                    >
                      {profileData?.valuebookLink}
                      <LinkIcon size={16} />
                    </a>
                  ) : (
                    <p className="text-gray-400 italic">Nije postavljeno - kliknite "Izmeni Profil" da dodate</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="mt-6 pt-6 border-t border-gray-200 flex gap-3">
              <button
                onClick={handleSave}
                disabled={saving || uploadingImage}
                className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {uploadingImage ? 'Otpremanje slike...' : saving ? 'Čuvanje...' : 'Sačuvaj'}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving || uploadingImage}
                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:bg-gray-100 disabled:cursor-not-allowed transition-colors font-medium"
              >
                Odustani
              </button>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 rounded-r-lg">
              <p className="text-green-800 font-medium">{successMessage}</p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg flex items-start gap-3">
              <AlertCircle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
              <p className="text-red-800">{error}</p>
            </div>
          )}
        </div>

        {/* Dashboard Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Current Balance Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Trenutno Stanje</h3>
              <Wallet className="text-blue-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(dashboardStats?.walletBalance || 0)}</p>
          </div>

          {/* Active Listings Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Aktivni Oglasi</h3>
              <ShoppingBag className="text-green-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.activeVouchers || 0}</p>
          </div>

          {/* Sold Vouchers Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Prodato Vaučera</h3>
              <TrendingUp className="text-purple-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.soldVouchers || 0}</p>
          </div>

          {/* Purchased Vouchers Card */}
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-600">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-gray-600">Kupljeno Vaučera</h3>
              <CheckCircle className="text-orange-600" size={24} />
            </div>
            <p className="text-2xl font-bold text-gray-900">{dashboardStats?.purchasedVouchers || 0}</p>
          </div>
        </div>

        {/* Rating Section */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Star className="text-amber-500" size={22} />
            <h2 className="text-xl font-bold text-gray-900">Ocene korisnika</h2>
          </div>

          {ratingStats?.count === 0 ? (
            <div className="text-center py-6">
              <Star className="mx-auto text-gray-300 mb-2" size={40} />
              <p className="text-gray-500 text-sm">Još nema ocena</p>
              <p className="text-gray-400 text-xs mt-1">Ocene se dobijaju nakon završenih pregovaranja</p>
            </div>
          ) : (
            <>
              {/* Average score */}
              <div className="flex items-center gap-4 mb-5 p-4 bg-amber-50 rounded-xl border border-amber-100">
                <div className="text-center">
                  <p className="text-4xl font-bold text-amber-600">{ratingStats?.average}</p>
                  <p className="text-xs text-amber-700 mt-0.5">od 5</p>
                </div>
                <div>
                  <div className="flex gap-0.5 mb-1">
                    {[1, 2, 3, 4, 5]?.map(star => (
                      <Star
                        key={star}
                        size={20}
                        className={star <= Math.round(ratingStats?.average || 0) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <p className="text-sm text-gray-600">{ratingStats?.count} {ratingStats?.count === 1 ? 'ocena' : ratingStats?.count < 5 ? 'ocene' : 'ocena'}</p>
                </div>
              </div>

              {/* Recent ratings */}
              {recentRatings?.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-gray-700">Poslednje ocene</h3>
                  {recentRatings?.map(r => (
                    <div key={r?.id} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        {r?.raterAvatar ? (
                          <img src={r?.raterAvatar} alt={r?.raterName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <User size={14} className="text-teal-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-medium text-gray-800 truncate">{r?.raterName}</span>
                          <div className="flex gap-0.5 flex-shrink-0">
                            {[1, 2, 3, 4, 5]?.map(star => (
                              <Star
                                key={star}
                                size={12}
                                className={star <= r?.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}
                                style={star <= r?.rating ? {color:'#CFB53B'} : {}}
                              />
                            ))}
                          </div>
                        </div>
                        {r?.comment && (
                          <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{r?.comment}</p>
                        )}
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(r?.createdAt)?.toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-xl shadow-lg mb-8 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Clock className="text-blue-600" size={24} />
              <h2 className="text-xl font-bold text-gray-900">Nedavne Transakcije</h2>
            </div>
          </div>
          
          <div className="divide-y divide-gray-200">
            {recentTransactions?.length > 0 ? (
              recentTransactions?.map((transaction) => (
                <div key={transaction?.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-base font-semibold text-gray-900 mb-1">
                        {transaction?.voucher?.title || 'Vaučer'}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">
                        {transaction?.voucher?.companyName || 'Kompanija'}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>{formatDate(transaction?.transactionDate)}</span>
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-medium">
                          {transaction?.isSeller ? 'Prodaja' : 'Kupovina'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">
                          {formatCurrency(transaction?.amount || 0)}
                        </p>
                        {getStatusBadge(transaction?.transactionStatus)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <Clock className="mx-auto text-gray-400 mb-3" size={48} />
                <p className="text-gray-500">Nemate nedavnih transakcija</p>
              </div>
            )}
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-5 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
          <div className="flex gap-3">
            <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm text-blue-900 font-medium mb-1">Informacije o profilu:</p>
              <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                <li>Lični podaci su prikazani na vrhu stranice, a aktivnosti ispod</li>
                <li>Polje <strong>Puno Ime</strong> je obavezno i mora biti popunjeno</li>
                <li>Polje <strong>Broj Telefona</strong> je opciono i možete ga ostaviti prazno</li>
                <li>Polje <strong>Ostali podaci</strong> ima limit od 50 karaktera za kratak opis</li>
                <li>Polje <strong>Link na platformu ValueBook</strong> se koristi za povezivanje sa ValueBook platformom</li>
                <li>Kliknite na dugme <strong>"Izmeni Profil"</strong> da promenite vaše podatke</li>
                <li>Vaša email adresa se koristi za prijavu i ne može se menjati</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}